import React, { useState, useEffect } from 'react';
import SuperPopup from '../SuperPopup';
import apiFetch from '../../src/js/fetch';
import BarLoader from 'react-spinners/BarLoader';
import Cookies from 'js-cookie';
import IcComplete from '../svg/IcComplete';
import icShare from '../../images/icons/ic-share-challenge.svg';
import { event as plausibleEvent } from '../../src/js/lib/plausible';

const CompleteAccount = ({ challengeSignUp = false, onClose }) => {
  const [open, setOpen] = useState(true);
  const [step, setStep] = useState(0);
  const [distanceChallenges, setDistanceChallenges] = useState([]);
  const [userDetails, setUserDetails] = useState({});
  const [loadingDistanceChallenges, setLoadingDistanceChallenges] =
    useState(true);
  const [steps, setSteps] = useState([]);
  const [blockingError, setBlockingError] = useState(null);
  const [countries, setCountries] = useState([]);
  const [error, setError] = useState(null);
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    if (open) {
      // The modal is now open on step 0.
      // Fetch distance challenges.
      fetchInitialUserData();
    }
  }, [open]);

  const updateData = async (data) => {
    let success = false;
    try {
      setProcessing(true);
      const result = await apiFetch('/api/complete-account', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      });
      console.log('Data updated successfully:', result);

      success = true;
    } catch (error) {
      console.error('Error updating data:', error);
      setError("Couldn't update your profile. Please try again later.");
    }

    setProcessing(false);

    return success;
  };

  const fetchInitialUserData = async () => {
    // Fetch distance challenges
    try {
      // Get user data and distance challenges.
      const data = await apiFetch('/api/complete-account');

      // Store distance challenges and countries in state.
      setDistanceChallenges(data.distance_challenges);
      setCountries(data.countries);

      if (data.user.name) {
        // User already has a name, so we don't need to ask for it again.
        // Close modal.
        closeModal();
      }

      const s = [];
      s.push(
        {
          title: challengeSignUp
            ? 'We need some more information before you can join the challenge'
            : 'Welcome to HurryUp',
          text: 'Please add your name, country and gender below.',
          type: 'info',
          nextButton: 'Save and continue',
          skipButton: false,
        },
        {
          title: 'Personalize your experience',
          text: 'By default, your name is shown on leaderboards and your profile. Add a username to stay more anonymous. Weight is used to calculate and display w/kg.',
          type: 'optional',
          nextButton: 'Save and continue',
          skipButton: true,
        }
      );

      if (!challengeSignUp && data.distance_challenges?.length) {
        // Only add the distance challenge step if there are distance challenges.
        s.push({
          title: 'Join our distance challenges',
          text: 'Track how far you go and see how you compare with others. Select the challenges you want to join.',
          type: 'distance',
          nextButton: 'Save and continue',
          skipButton: true,
        });
      }

      s.push({
        title: 'You’re in, now challenge your friends!',
        text: 'Racing strangers is fun, but beating your friends is next-level.',
        type: 'ready',
        nextButton: 'Close',
      });

      setSteps(s);
    } catch (error) {
      console.error('Error fetching distance challenges:', error);
      setBlockingError(true);
    }
    setLoadingDistanceChallenges(false);
  };

  const closeModal = () => {
    // Trigger callback if provided.
    const completedNecessaryInfo =
      !!userDetails.name && !!userDetails.gender && !!userDetails.country;

    if (onClose) {
      // Modal is triggered by a parent component.
      // Pass the "completed necessary info"-flag to the parent component, and close the modal.
      setOpen(false);
      onClose(completedNecessaryInfo);
    } else if (completedNecessaryInfo) {
      // Refresh page to show the updated user data.
      location.reload();
    }
  };

  const onSubmit = async (e) => {
    e.preventDefault();

    if (step < steps.length) {
      // Not last step. Submit, save, and go to next step.
      const currentStep = steps[step];
      if (currentStep.type === 'info') {
        // Save required information, and sign up to selected global distance challenges.
        const splitName = userDetails.name.split(' ');
        const data = {
          user: {
            firstname: splitName.shift(),
            lastname: splitName.join(' '),
            country: userDetails.country,
            gender: userDetails.gender,
          },
        };

        // Update user data.
        const success = await updateData(data);
        if (success) {
          setStep((prevStep) => prevStep + 1);
        }
      } else if (currentStep.type === 'optional') {
        // Save optional information.
        if (userDetails.username || userDetails.weight) {
          const data = {
            user: {
              username: userDetails.username,
              weight: userDetails.weight,
            },
          };

          // Update user data.
          const success = await updateData(data);
          if (success) {
            setStep((prevStep) => prevStep + 1);
          }
        } else {
          // No optional information provided, just go to the next step.
          console.log('No optional information provided');
          setStep((prevStep) => prevStep + 1);
        }
      } else if (currentStep.type === 'distance') {
        // Sign up to selected global distance challenges.
        const data = {
          distance_challenge_ids: userDetails.distanceChallengeIds,
        };

        // Update user data.
        const success = await updateData(data);
        if (success) {
          setStep((prevStep) => prevStep + 1);
        }
      } else if (currentStep.type === 'ready') {
        // Last step, close. No more information to save.

        closeModal();
      } else {
        // Unknown step, do nothing.
        alert('Unknown step');
        console.error('Unknown step:', currentStep);
      }
    } else {
      // Unknown step, do nothing.
      alert('After last step?!?');
      console.error('After last step:', step);
    }
  };

  const onChange = (e) => {
    const target = e.target;

    if (target.name === 'distance-challenge') {
      // Distance challenge selected ids
      const selectedChallenges = Array.from(
        document.querySelectorAll('input[name="distance-challenge"]:checked')
      ).map((checkbox) => parseInt(checkbox.value));
      console.log('Selected challenges:', selectedChallenges);

      setUserDetails((prevDetails) => ({
        ...prevDetails,
        distanceChallengeIds: selectedChallenges,
      }));
    } else {
      const value = target.type === 'checkbox' ? target.checked : target.value;
      const name = target.name;

      setUserDetails((prevDetails) => ({
        ...prevDetails,
        [name]: value,
      }));
    }
  };

  const currentStep = steps.length ? steps[step] : null;

  const onCloseModalClick = () => {
    // Do nothing. We don't allow closing the modal on outside click, so send in an empty function.
    console.log('Dont close modal');
  };

  const shareHurryUp = async () => {
    const shareData = {
      title: `Compete with me in HurryUp!`,
      text: `Compete across multiple segments on a single leaderboard.`,
      url: `https://bit.ly/44HySfN`,
    };

    try {
      await navigator.share(shareData);
      plausibleEvent('Signup - Share');
    } catch (err) {
      console.log('Canceled share');
    }
  };

  return (
    <>
      <SuperPopup
        open={open}
        onClose={onCloseModalClick}
        enableCloseButton={false}
        py='pb-6'
      >
        {blockingError ? (
          <div className='w-full flex justify-center mb-20'>
            <p className='text-text-grey'>
              Something went wrong. Please try again later.
            </p>
          </div>
        ) : currentStep ? (
          <>
            {currentStep.type === 'ready' ? (
              <div className='flex w-full justify-center'>
                <IcComplete />
              </div>
            ) : (
              <span className='body-text'>
                {step + 1} / {steps.length}
              </span>
            )}
            <form className='' method='POST' onSubmit={onSubmit}>
              <h2 className='mini-heading mt-6 mb-4'>{currentStep.title}</h2>
              <p className='mb-4'>{currentStep.text}</p>
              {currentStep.type === 'distance' ? (
                <div className=''>
                  {loadingDistanceChallenges ? (
                    <p>Loading...</p>
                  ) : (
                    <>
                      {distanceChallenges?.length ? (
                        <ul
                          className={`grid grid-cols-2 ${
                            distanceChallenges.length === 3
                              ? 'md:grid-cols-3 min-h-[14rem]'
                              : ''
                          } gap-1`}
                        >
                          {distanceChallenges.map((challenge) => {
                            const checked =
                              userDetails.distanceChallengeIds?.includes(
                                challenge.id
                              ) || false;
                            return (
                              <li
                                key={challenge.id}
                                className='flex flex-1 mini-text font-medium'
                              >
                                <label
                                  className={`flex flex-col flex-1 justify-center items-center border cursor-pointer rounded px-4 py-8 ${
                                    checked
                                      ? 'bg-accent border-2 border-border-tertiary'
                                      : 'border-border'
                                  }`}
                                >
                                  <span>
                                    <img
                                      className='h-8 mb-4'
                                      src={
                                        challenge.distance_challenge_type?.icon
                                      }
                                    />
                                  </span>
                                  <span>{challenge.name}</span>
                                  <input
                                    type='checkbox'
                                    name='distance-challenge'
                                    className='sr-only'
                                    value={challenge.id}
                                    onChange={onChange}
                                    checked={checked}
                                  />
                                </label>
                              </li>
                            );
                          })}
                        </ul>
                      ) : (
                        <p className='text-text-grey'>
                          No available global distance challenges just now. You
                          can join them later when browsing challenges if you'd
                          like!
                        </p>
                      )}
                    </>
                  )}
                </div>
              ) : currentStep.type === 'info' ? (
                <div className='text-left'>
                  <div className='w-full mb-4'>
                    <label htmlFor='name' className='input-label'>
                      Name <span className='text-error'>*</span>
                    </label>
                    <input
                      id='name'
                      key='name'
                      value={userDetails.name || ''}
                      name='name'
                      onChange={onChange}
                      type='text'
                      placeholder='Your name'
                      className='input w-full'
                      required={true}
                    />
                  </div>
                  <div className='w-full mb-4'>
                    <label htmlFor='country' className='input-label'>
                      Country <span className='text-error'>*</span>
                    </label>
                    <select
                      key='country'
                      id='country'
                      value={userDetails.country || ''}
                      name='country'
                      onChange={onChange}
                      className='w-full'
                      required={true}
                    >
                      <option value=''>Select country</option>
                      {countries.map((country) => (
                        <option key={country.code} value={country.code}>
                          {country.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className='w-full mb-4'>
                    <label htmlFor='gender' className='input-label'>
                      Gender <span className='text-error'>*</span>
                    </label>
                    <select
                      key='gender'
                      id='gender'
                      value={userDetails.gender || ''}
                      name='gender'
                      onChange={onChange}
                      className='w-full'
                      required={true}
                    >
                      <option value=''>Select your gender</option>
                      <option value='male'>Male</option>
                      <option value='female'>Female</option>
                      <option value='other'>Other</option>
                    </select>
                  </div>
                </div>
              ) : currentStep.type === 'optional' ? (
                <div className='text-left'>
                  <div className='w-full mb-4'>
                    <label htmlFor='username' className='input-label'>
                      Username (optional)
                    </label>
                    <input
                      key={'username'}
                      id='username'
                      value={userDetails.username || ''}
                      name='username'
                      onChange={onChange}
                      type='text'
                      placeholder='Your username'
                      className='input w-full'
                    />
                  </div>
                  <div className='w-full mb-4'>
                    <label htmlFor='weight' className='input-label'>
                      Weight (optional)
                    </label>
                    <input
                      key={'weight'}
                      id='weight'
                      value={userDetails.weight || ''}
                      name='weight'
                      onChange={onChange}
                      type='number'
                      placeholder='Your weight'
                      className='input w-full'
                    />
                  </div>
                </div>
              ) : currentStep.type === 'ready' ? (
                <div className='flex justify-center w-full text-center'>
                  <button
                    className='group text-text-grey-600 flex items-center mb-4'
                    type='button'
                    onClick={shareHurryUp}
                  >
                    <span className='underline group-hover:no-underline'>
                      Share HurryUp now
                    </span>
                    <img src={icShare} className='ml-1 w-6' />
                  </button>
                </div>
              ) : (
                <p>Unknown step!</p>
              )}

              <div className='flex flex-col items-center gap-4 mt-6'>
                <button className='button dark'>
                  <span className='px-4'>{currentStep.nextButton}</span>
                </button>
                {currentStep.skipButton ? (
                  <button
                    type='button'
                    className='text-text-grey underline'
                    onClick={() => setStep((prevStep) => prevStep + 1)}
                  >
                    Skip for now
                  </button>
                ) : null}
              </div>

              {error ? <p className='text-text-grey mt-4'>{error}</p> : null}
            </form>
          </>
        ) : (
          <div className='w-full flex justify-center mb-20'>
            <BarLoader />
          </div>
        )}
      </SuperPopup>
    </>
  );
};

export default CompleteAccount;
